import React, { useState, Children } from "react";
import routes from "shared/utils/constants/routes";
import { useHistory, useParams } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import InputGroup from "views/components/inputGroup/InputGroup";
import {
  GeneralSettingsForm,
  getBase64,
} from "views/customizeBackground/generalSettings";
import ScorecardSettings from "views/customizeScoreCard/scorecardSettingsV2";
import { TableSettingsForm } from "views/customizeSummaryTables/tablesSettings";
import {
  createTheme,
  getTheme,
  updateTheme,
  saveImage,
} from "services/publicLiveScores";
import {
  success as Success,
  error as Error,
} from "views/components/toast/toast";
import { families } from "shared/utils/constants/sizeFonts";
import { useQuery } from "@tanstack/react-query";
import queryKeys from "shared/utils/constants/queryKeys";

import themeStyles from "shared/styles/theme/theme-styles.module.scss";
import loadingImg from "shared/images/loading1.svg";
import styles from "./styles.module.scss";

const Tabs = ({ children, onChange, activeTab }) => {
  const tabs = Children.map(children, (child, index) => ({
    title: child.props.title,
    content: child.props.children,
    key: index,
  }));

  return (
    <>
      <div className={styles.tabList}>
        {tabs.map((tab, index) => (
          <button
            key={index}
            className={activeTab === index ? styles.active : ""}
            onClick={() => onChange(index)}
          >
            {tab.title}
          </button>
        ))}
      </div>
      <div className={styles.tabContent}>
        {tabs[activeTab] && <div>{tabs[activeTab].content}</div>}
      </div>
    </>
  );
};
// This component is more of a placeholder for props and doesn't render anything itself.
const TabPanel = ({ children }) => children;

const validationSchema = Yup.object().shape({
  theme_name: Yup.string().required("Theme name is required"),
});

const CreateEditTheme = () => {
  const history = useHistory();
  const params = useParams();
  const editMode = history.location.pathname.includes("/edit/") && !!params.id;
  const [activeTab, setActiveTab] = useState(0);

  const {
    values,
    handleChange,
    handleBlur,
    errors,
    setFieldValue,
    setFieldError,
    handleSubmit,
  } = useFormik({
    initialValues: {
      theme_name: "",
      global: {
        ten_frames_scorecard: true,
        compact_scorecard: true,
        list_players_top: true,
        list_players_last_three: true,
        list_players_top_low: true,
        background: {
          type: "color",
          color: "#999",
          image: null,
          imageFile: null,
        },
      },
      scorecard: {
        frame_score: {
          font_bold: true,
          font_size: "20px",
          font_color: "#000000",
        },
        player_name: {
          font_bold: true,
          font_size: "20px",
          font_color: "#00ff85",
          font_family: families[0],
          font_style: "normal",
        },
        current_frame: {
          low_frame_background: "#8df481",
          top_frame_background: "#42c56f",
        },
        throws_style: {
          font_bold: true,
          font_size: "16px",
          font_color: "#000000",
        },
        show_lane_number: true,
        top_frame_background: "#5c15e7",
        frames_container_background: "#d71313",
        scores_container_background: "#ffffff",
        section_container_background: "#2d4438",
        game_label: {
          font_bold: true,
          font_color: "#B3CEB3",
          font_family: families[0],
          font_size: "16px",
        },
        total_score: {
          font_bold: true,
          color: "#00ff85",
          font_family: families[0],
          font_size: "20px",
          background_color: "#0B1D01",
        },
        total_label: {
          font_bold: true,
          color: "#00ff85",
          font_family: families[0],
          font_size: "14px",
          background_color: "#ffffff",
        },
      },
      table: {
        evenRows: {
          background: "#8a86e9",
          fontSize: "16px",
          color: "#cc1f1f",
          fontWeight: "normal",
        },
        oddRows: {
          background: "#ffffff",
          fontSize: "14px",
          color: "#000000",
          fontWeight: "700",
        },
        maxScore: {
          background: "#6c4dc9",
          color: "#417505",
          fontSize: "16px",
          fontWeight: "700",
          fontFamily: "Futura PT Cond",
        },
        maxScore_label: {
          color: "#000000",
          fontSize: "14px",
          fontWeight: "700",
        },
        frameContainer: {
          background: "#999999",
          fontSize: "10px",
          color: "#f7f7f7",
          fontWeight: "700",
        },
        sectionContainerBackground: "#313131",
      },
    },
    validateOnChange: false,
    validationSchema,
    onSubmit: async ({ scorecard, global, table, theme_name }) => {
      const body = new FormData();
      let urlImage = null;

      if (!!global.background.imageFile) {
        body.append("file", global.background.imageFile);
        const response = await saveImage(body);
        urlImage = response.url;
      }

      const finalValues = {
        theme_name,
        scorecard_settings: {
          ...scorecard,
          // new props
          inner_background: "#fff",
          bowlingcenter: {
            background_color: "#fff",
            color: "#000",
            font_size: "13px",
            font_weight: "normal",
          },
          total_score: {
            ...scorecard.total_score,
            font_weight: scorecard.total_score.font_bold ? "700" : "normal",
          },
          total_label: {
            ...scorecard.total_label,
            font_weight: scorecard.total_label.font_bold ? "700" : "normal",
          },
          font_family: "Futura PT Cond",
          lane_background: table.sectionContainerBackground,
        },
        livescores_settings: {
          ...global,
          background: {
            type: global.background.type,
            ...(global.background.type === "color" && {
              color: global.background.color,
            }),
            ...(global.background.type === "image" && {
              image: !!global.background.imageFile
                ? urlImage
                : global.background.image,
            }),
          },
        },
      };
      // add finalValues to body
      body.append("theme", JSON.stringify(finalValues));

      const request = editMode
        ? updateTheme({
            id: params.id,
            data: finalValues,
          })
        : createTheme(finalValues);
      try {
        const response = await request;
        if (response) {
          history.push(routes.Themes.LIST);
          Success("Theme created successfully");
        }
      } catch (error) {
        Error("An error occurred while creating the theme");
      }
    },
  });

  // query to get the theme data from the API using the id in the URL
  const { data, loading } = useQuery({
    queryKey: [queryKeys.themes.get, params.id],
    queryFn: () => getTheme(params.id),
    refetchOnWindowFocus: false,
    enabled: editMode,
    select: ({ data }) => data,
    onSuccess: (data) => {
      setFieldValue("theme_name", data.theme_name);
      setFieldValue("global", data.livescores_settings);

      const { total_score, total_label, ...rest } = data.scorecard_settings;
      const { font_weight, ...restTotalScore } = total_score;
      const { font_weight: font_weight_label, ...restTotalLabel } = total_label;
      setFieldValue("scorecard", {
        ...rest,
        total_score: {
          ...restTotalScore,
          font_bold: total_score.font_weight === "700",
        },
        total_label: {
          ...restTotalLabel,
          font_bold: total_label.font_weight === "700",
        },
      });
    },
  });

  const handleNextAction = () => {
    if (activeTab === 2) {
      return handleSubmit();
    }
    setActiveTab((prev) => prev + 1);
  };

  // Tab 1: Global Settings methods
  const onSelectBackground = (field, value = null) => {
    setFieldValue("global.background.type", field);
    setFieldValue(`global.background.${field}`, value);
  };
  const onChangeImage = (image) => {
    if (image.size > 10000000) {
      setFieldError(
        "global.background.image",
        "The uploaded image cannot exceed a size of 10M",
      );
      return;
    }

    setFieldValue("global.background.imageFile", image);
    getBase64(image).then((base64) => {
      onSelectBackground("image", base64);
    });
  };

  // Tab 2: Scorecard Settings methods
  const onChangeScorecardColor = (typeKey) => (color) => {
    setFieldValue(`scorecard.${typeKey}`, color.hex);
  };
  const onChangeScorecardSize = (type) => (evt) => {
    setFieldValue(`scorecard.${type}.font_size`, evt.target.value);
  };
  const onChangeScorecardWeight = (type) => (value) => {
    setFieldValue(`scorecard.${type}.font_bold`, value);
  };
  const onChangeFontFamily =
    (type) =>
    ({ target }) => {
      setFieldValue(`scorecard.${type}.font_family`, target.value);
    };

  // Tab 3: Table Settings methods
  const onChangeTableColor = (typeKey) => (color) => {
    setFieldValue(`table.${typeKey}`, color.hex);
  };
  const onChangeTableFontSize = (type) => (evt) => {
    setFieldValue(`table.${type}.fontSize`, evt.target.value);
  };
  const onChangeTableFontWeight = (type) => (value) => {
    setFieldValue(`table.${type}.fontBold`, value);
  };

  const ButtonLabel = () => {
    if (activeTab === 2) {
      return editMode ? "Edit theme" : "Create theme";
    }
    return "Save and Continue";
  };

  if (editMode && (loading || !data)) {
    return (
      <div className={styles.container}>
        <div className={styles.lines} />
        <div className={styles.content}>
          <img src={loadingImg} alt="" width="100%" height="137px"></img>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.lines} />

      <div className={styles.content}>
        <div className={styles.themeNameInput}>
          <InputGroup
            name="theme_name"
            label="Theme name"
            leftMargin={false}
            inputValue={values.theme_name}
            onChange={handleChange}
            onBlur={handleBlur}
            className={styles.input}
            placeholder="e.g. PBA theme"
            errors={errors}
            touched={{ theme_name: true }}
          />
        </div>

        <div className={styles.tabsContainer}>
          <Tabs activeTab={activeTab} onChange={setActiveTab}>
            <TabPanel title="Global Settings">
              <GeneralSettingsForm
                formik={{
                  values: values.global,
                  handleChange: (evt) =>
                    handleChange({
                      target: {
                        name: "global",
                        value: {
                          ...values.global,
                          [evt.target.name]: evt.target.checked,
                        },
                      },
                    }),
                  errors: errors.global,
                }}
                onSelectBackground={onSelectBackground}
                initialImage={null}
                onChangeImage={onChangeImage}
              />
            </TabPanel>
            <TabPanel title="Scorecard">
              <ScorecardSettings
                onChangeColor={onChangeScorecardColor}
                onChangeFontSize={onChangeScorecardSize}
                onChangeFontWeight={onChangeScorecardWeight}
                onChangeFontFamily={onChangeFontFamily}
                cardStyles={{
                  ...values.scorecard,
                  screenColor: values.global.background.color,
                }}
                showCompactVersion={values.global.compact_scorecard}
              />
            </TabPanel>
            <TabPanel title="Score table">
              <TableSettingsForm
                onChangeColor={onChangeTableColor}
                onChangeFontSize={onChangeTableFontSize}
                onChangeFontWeight={onChangeTableFontWeight}
                tableStyles={values.table}
              />
            </TabPanel>
          </Tabs>
          <div className={styles.formActions}>
            <button
              type="button"
              className={`${themeStyles.btnOutlinedExpandable}`}
              onClick={() => history.push(routes.Themes.LIST)}
            >
              Cancel
            </button>
            <button
              type="submit"
              className={`${themeStyles.btnPrimaryLT}`}
              onClick={handleNextAction}
              style={{ width: 323 }}
            >
              <ButtonLabel />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateEditTheme;
